/**
 * InvestorComparePreview
 *
 * Side-by-side comparison of two investor tiers (Lite vs Pro, Pro vs
 * Plus, or Lite vs Plus). Helps prospects who can already picture
 * themselves on the platform decide WHICH tier fits.
 *
 * Each column shows the same grouped feature checklist. Cells are
 * checkmarks (included) or em-dashes (not included), so visitors can
 * scan top-to-bottom on each side and immediately see where the tiers
 * diverge. Includes group toggles to filter to "what's different"
 * vs "show everything", and a counter summary at the top.
 *
 * Source of truth: `INVESTOR_PLANS_FEATURE_MATRIX` from
 * `components/investor/InvestorPlansComparisonTable.jsx` — the same
 * data backs the full /pricing and /for-investors comparison tables.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
    X,
    ArrowUpRight,
    GitCompareArrows,
    Crown,
    Lock,
    Check,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { createCheckout } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { TIER_META } from "./sampleData";
import { INVESTOR_PLANS_FEATURE_MATRIX } from "@/components/investor/InvestorPlansComparisonTable";

// Each combo is rendered as "left" vs "right". Lower tier always on
// the left so the upgrade visual reads as "→ unlock more".
export const COMPARE_COMBOS = {
    "lite-vs-pro": { left: "lite", right: "pro" },
    "pro-vs-plus": { left: "pro", right: "plus" },
    "lite-vs-plus": { left: "lite", right: "plus" },
};

function tierBadge(tier) {
    if (tier === "pro") {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bl-primary text-bl-surface text-[9px] tracking-[0.16em] uppercase font-medium"
                data-testid={`compare-badge-${tier}`}
            >
                Most Popular
            </span>
        );
    }
    if (tier === "plus") {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bl text-bl-surface text-[9px] tracking-[0.16em] uppercase font-medium"
                data-testid={`compare-badge-${tier}`}
            >
                <Crown className="w-2.5 h-2.5" />
                Serious Investors
            </span>
        );
    }
    return null;
}

function FeatureCell({ included }) {
    return included ? (
        <Check className="w-4 h-4 text-bl-primary inline" aria-hidden />
    ) : (
        <span className="text-bl-muted" aria-hidden>
            —
        </span>
    );
}

function ColumnHeader({ tier }) {
    const meta = TIER_META[tier];
    return (
        <div
            className={
                "px-4 sm:px-5 py-4 border-b border-bl flex items-start justify-between gap-3 " +
                (tier === "pro"
                    ? "bg-bl-teal-tint/60"
                    : tier === "plus"
                      ? "bg-bl-teal-tint/30"
                      : "bg-bl-bg/50")
            }
        >
            <div className="min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                    {meta.tagline}
                </div>
                <div className="font-display font-semibold text-lg text-bl">
                    {meta.name}
                </div>
                <div className="mt-0.5 font-mono-bl text-xs text-bl-muted">
                    {meta.price}
                    {meta.per}
                </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
                {tierBadge(tier)}
            </div>
        </div>
    );
}

function ColumnFooter({ tier, onChoose, loading }) {
    const meta = TIER_META[tier];
    return (
        <div className="px-4 sm:px-5 py-3.5 border-t border-bl bg-bl-surface">
            <button
                type="button"
                onClick={() => onChoose(meta.id)}
                disabled={loading}
                className={
                    (tier === "pro" ? "bl-btn-primary" : "bl-btn-secondary") +
                    " w-full"
                }
                data-testid={`compare-choose-${meta.id}`}
            >
                {loading ? "Redirecting…" : `Choose ${meta.name}`}
                <ArrowUpRight className="w-4 h-4" />
            </button>
        </div>
    );
}

function ComboTabs({ combo, onChange }) {
    const tabs = [
        { id: "lite-vs-pro", label: "Lite vs Pro" },
        { id: "pro-vs-plus", label: "Pro vs Plus" },
        { id: "lite-vs-plus", label: "Lite vs Plus" },
    ];
    return (
        <div
            className="inline-flex flex-wrap items-center gap-1 p-1 rounded-full bg-bl-bg border border-bl"
            role="tablist"
            aria-label="Compare combination"
            data-testid="compare-combo-tabs"
        >
            {tabs.map((t) => (
                <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={combo === t.id}
                    onClick={() => onChange(t.id)}
                    className={
                        "px-3 py-1.5 min-h-[36px] rounded-full text-xs transition-colors " +
                        (combo === t.id
                            ? "bg-bl text-bl-surface font-medium"
                            : "text-bl-muted hover:text-bl")
                    }
                    data-testid={`compare-combo-tab-${t.id}`}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );
}

// Counts how many features each tier includes across the entire matrix.
// Drives the "Lite includes X of N features" header chip.
function countIncluded(matrix, tier) {
    let total = 0;
    let on = 0;
    for (const g of matrix) {
        for (const row of g.rows) {
            total += 1;
            if (row[tier]) on += 1;
        }
    }
    return { on, total };
}

// Filters the matrix to only rows where the two tiers DIFFER. Lets
// the visitor cut through identical rows and focus on the upgrade
// delta.
function buildDiffMatrix(matrix, leftTier, rightTier) {
    return matrix
        .map((g) => ({
            group: g.group,
            rows: g.rows.filter((r) => r[leftTier] !== r[rightTier]),
        }))
        .filter((g) => g.rows.length > 0);
}

export default function InvestorComparePreview({ combo, open, onOpenChange, source }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(null);
    const [activeCombo, setActiveCombo] = useState(combo || "lite-vs-pro");
    const [viewMode, setViewMode] = useState("differences"); // "differences" | "all"
    const openedAtRef = useRef(null);

    useEffect(() => {
        if (combo) setActiveCombo(combo);
    }, [combo]);

    useEffect(() => {
        if (!open) return undefined;
        openedAtRef.current = Date.now();
        track("investor_compare_preview_opened", {
            combo: activeCombo,
            source,
        });
        return () => {
            const dwellMs = Date.now() - (openedAtRef.current || Date.now());
            track("investor_compare_preview_closed", {
                combo: activeCombo,
                source,
                dwell_ms: dwellMs,
                dwell_seconds: Math.round(dwellMs / 1000),
            });
        };
    }, [open, activeCombo, source]);

    const { left, right } =
        COMPARE_COMBOS[activeCombo] || COMPARE_COMBOS["lite-vs-pro"];

    const visibleMatrix = useMemo(() => {
        if (viewMode === "all") return INVESTOR_PLANS_FEATURE_MATRIX;
        return buildDiffMatrix(INVESTOR_PLANS_FEATURE_MATRIX, left, right);
    }, [viewMode, left, right]);

    const leftCount = useMemo(
        () => countIncluded(INVESTOR_PLANS_FEATURE_MATRIX, left),
        [left],
    );
    const rightCount = useMemo(
        () => countIncluded(INVESTOR_PLANS_FEATURE_MATRIX, right),
        [right],
    );

    const handleChoose = async (planId) => {
        track("investor_compare_preview_cta_clicked", {
            combo: activeCombo,
            source,
            cta_target: planId,
        });
        try {
            if (!user) {
                navigate(`/auth/login?next=/pricing#investor-plans`);
                onOpenChange(false);
                return;
            }
            setLoading(planId);
            const r = await createCheckout(planId);
            window.location.href = r.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout.");
            setLoading(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-[1100px] w-[96vw] sm:w-[94vw] h-[92vh] sm:h-[88vh] p-0 gap-0 bg-bl-surface border-bl rounded-xl overflow-hidden flex flex-col"
                data-testid={`investor-compare-dialog-${activeCombo}`}
            >
                <VisuallyHidden.Root>
                    <DialogTitle>Compare investor tiers side-by-side</DialogTitle>
                    <DialogDescription>
                        Side-by-side feature comparison of two investor
                        subscription tiers. Switch combos via the tabs.
                    </DialogDescription>
                </VisuallyHidden.Root>

                {/* Top bar */}
                <div className="sticky top-0 z-10 px-4 sm:px-7 py-3.5 border-b border-bl bg-bl-surface/95 backdrop-blur flex items-center justify-between gap-3">
                    <div className="min-w-0 flex items-center gap-2.5">
                        <div
                            className="w-9 h-9 rounded-lg bg-bl-primary text-bl-surface flex items-center justify-center shrink-0"
                            aria-hidden
                        >
                            <GitCompareArrows className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                Side-by-side comparison
                            </div>
                            <div className="font-display font-semibold text-bl text-sm sm:text-base truncate">
                                Compare investor tiers
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="w-9 h-9 rounded-full hover:bg-bl-bg flex items-center justify-center text-bl-muted hover:text-bl transition-colors"
                        aria-label="Close comparison"
                        data-testid="compare-close-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Tabs row + view-mode toggle */}
                <div className="px-4 sm:px-7 py-3 border-b border-bl bg-bl-bg flex items-center justify-between gap-3 flex-wrap">
                    <ComboTabs
                        combo={activeCombo}
                        onChange={(c) => {
                            setActiveCombo(c);
                            track("investor_compare_preview_combo_changed", {
                                combo: c,
                                source,
                            });
                        }}
                    />
                    <div
                        className="inline-flex items-center gap-1 p-1 rounded-full bg-bl-surface border border-bl"
                        role="tablist"
                        aria-label="View mode"
                        data-testid="compare-view-mode-tabs"
                    >
                        {[
                            { id: "differences", label: "Differences only" },
                            { id: "all", label: "All features" },
                        ].map((v) => (
                            <button
                                key={v.id}
                                type="button"
                                role="tab"
                                aria-selected={viewMode === v.id}
                                onClick={() => {
                                    setViewMode(v.id);
                                    track(
                                        "investor_compare_view_mode_changed",
                                        { view_mode: v.id, combo: activeCombo },
                                    );
                                }}
                                className={
                                    "px-3 py-1.5 min-h-[36px] rounded-full text-xs transition-colors " +
                                    (viewMode === v.id
                                        ? "bg-bl text-bl-surface font-medium"
                                        : "text-bl-muted hover:text-bl")
                                }
                                data-testid={`compare-view-mode-${v.id}`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-bl-muted">
                        <Lock className="w-3 h-3" />
                        No signup needed
                    </span>
                </div>

                {/* Summary chips */}
                <div className="px-4 sm:px-7 py-3 border-b border-bl bg-bl-paper grid grid-cols-2 gap-3">
                    <div
                        className="text-xs sm:text-sm text-bl-muted"
                        data-testid="compare-summary-left"
                    >
                        <span className="font-display font-medium text-bl">
                            {TIER_META[left].name}
                        </span>{" "}
                        includes{" "}
                        <span className="font-mono-bl text-bl">
                            {leftCount.on} of {leftCount.total}
                        </span>{" "}
                        features
                    </div>
                    <div
                        className="text-xs sm:text-sm text-bl-muted text-right sm:text-left"
                        data-testid="compare-summary-right"
                    >
                        <span className="font-display font-medium text-bl-primary">
                            {TIER_META[right].name}
                        </span>{" "}
                        includes{" "}
                        <span className="font-mono-bl text-bl">
                            {rightCount.on} of {rightCount.total}
                        </span>{" "}
                        features
                    </div>
                </div>

                {/* Body — two columns with synchronized feature rows */}
                <div
                    className="flex-1 overflow-hidden bl-paper"
                    data-testid={`compare-body-${activeCombo}`}
                >
                    <div className="h-full grid grid-cols-1 md:grid-cols-2 relative">
                        <div className="border-b md:border-b-0 md:border-r border-bl flex flex-col">
                            <ColumnHeader tier={left} />
                        </div>
                        <div className="flex flex-col">
                            <ColumnHeader tier={right} />
                        </div>

                        {/* Scrollable shared body */}
                        <div className="col-span-1 md:col-span-2 overflow-y-auto">
                            {visibleMatrix.length === 0 ? (
                                <div className="p-8 text-center text-sm text-bl-muted">
                                    No differences between these tiers.
                                </div>
                            ) : (
                                <table className="w-full text-xs sm:text-sm">
                                    <tbody>
                                        {visibleMatrix.map((g) => (
                                            <Fragment key={g.group}>
                                                <tr className="bg-bl-bg/40">
                                                    <td
                                                        colSpan={3}
                                                        className="px-4 sm:px-5 py-2 text-[10px] tracking-[0.18em] uppercase text-bl-primary font-medium border-b border-bl"
                                                    >
                                                        {g.group}
                                                    </td>
                                                </tr>
                                                {g.rows.map((row) => (
                                                    <tr
                                                        key={`${g.group}::${row.label}`}
                                                        className="border-b border-bl last:border-0"
                                                        data-testid={`compare-feature-row-${row.label}`}
                                                    >
                                                        <td className="px-4 sm:px-5 py-2.5 w-[44%] text-bl leading-snug">
                                                            {row.label}
                                                        </td>
                                                        <td
                                                            className={
                                                                "px-4 sm:px-5 py-2.5 w-[28%] text-center md:border-r border-bl " +
                                                                (left === "pro"
                                                                    ? "bg-bl-teal-tint/10"
                                                                    : "")
                                                            }
                                                        >
                                                            <FeatureCell
                                                                included={row[left]}
                                                            />
                                                        </td>
                                                        <td
                                                            className={
                                                                "px-4 sm:px-5 py-2.5 w-[28%] text-center " +
                                                                (right === "pro"
                                                                    ? "bg-bl-teal-tint/10"
                                                                    : "")
                                                            }
                                                        >
                                                            <FeatureCell
                                                                included={
                                                                    row[right]
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footers — stick at the bottom under the body */}
                <div className="grid grid-cols-1 md:grid-cols-2 border-t border-bl">
                    <div className="md:border-r border-bl">
                        <ColumnFooter
                            tier={left}
                            onChoose={handleChoose}
                            loading={loading === TIER_META[left].id}
                        />
                    </div>
                    <ColumnFooter
                        tier={right}
                        onChoose={handleChoose}
                        loading={loading === TIER_META[right].id}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Reusable trigger button — used in /pricing and the existing single-tier
// preview header.
export function CompareTiersButton({ onClick, label, testid, variant = "ghost" }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={
                "inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors min-h-[44px] px-4 py-2.5 rounded-full " +
                (variant === "primary"
                    ? "bg-bl-primary text-bl-surface hover:bg-bl-primary-dark"
                    : "text-bl-primary bg-bl-teal-tint hover:bg-bl-teal-tint/70 border border-bl-primary/15")
            }
            data-testid={testid}
        >
            <GitCompareArrows className="w-4 h-4" />
            {label || "Compare tiers side-by-side"}
        </button>
    );
}
